/**
 * Universe Theme Presets
 * 
 * Defines visual configurations for different constellation vibes.
 * Each theme controls background, effects, motion, lines, and node appearance.
 */

export const UNIVERSE_THEMES = {
  classic: {
    id: "classic",
    label: "Classic",
    bg: { nebula: false, starfieldDensity: 60, grain: 0.05 },
    fx: { glow: 0.35, shimmer: 0.0, ripples: false },
    motion: { twinkle: 0.15, drift: 0.05, pulse: 0.15 },
    lines: { opacity: 0.55, width: 1.0, energy: 0.0 },
    nodes: { baseRadius: 1.0, aura: 0.5 },
  },

  living: {
    id: "living",
    label: "Living",
    bg: { nebula: true, starfieldDensity: 120, grain: 0.08 },
    fx: { glow: 0.55, shimmer: 0.25, ripples: true },
    motion: { twinkle: 0.55, drift: 0.25, pulse: 0.35 },
    lines: { opacity: 0.65, width: 1.1, energy: 0.15 },
    nodes: { baseRadius: 1.05, aura: 0.8 },
  },

  neon: {
    id: "neon",
    label: "Neon",
    bg: { nebula: false, starfieldDensity: 90, grain: 0.06 },
    fx: { glow: 0.95, shimmer: 0.65, ripples: false },
    motion: { twinkle: 0.35, drift: 0.15, pulse: 0.75 },
    lines: { opacity: 0.85, width: 1.25, energy: 0.55 },
    nodes: { baseRadius: 1.1, aura: 1.0 },
  },

  living_neon: {
    id: "living_neon",
    label: "Living × Neon",
    bg: { nebula: true, starfieldDensity: 150, grain: 0.1 },
    fx: { glow: 1.0, shimmer: 0.75, ripples: true },
    motion: { twinkle: 0.65, drift: 0.25, pulse: 0.85 },
    lines: { opacity: 0.9, width: 1.3, energy: 0.7 },
    nodes: { baseRadius: 1.12, aura: 1.05 },
  },
};

export const DEFAULT_UNIVERSE_THEME = "living_neon";

/**
 * Apply safety knobs without changing the renderer.
 * 
 * @param {string} themeId - Theme ID (classic, living, neon, living_neon)
 * @param {object} opts - Options { reduceMotion, powerSaver }
 * @returns {object} Resolved theme configuration
 */
export function resolveUniverseTheme(themeId, opts) {
  const base = UNIVERSE_THEMES[themeId] || UNIVERSE_THEMES[DEFAULT_UNIVERSE_THEME];
  const reduceMotion = !!opts?.reduceMotion;
  const powerSaver = !!opts?.powerSaver;

  // Clone shallow
  const t = JSON.parse(JSON.stringify(base));

  if (reduceMotion) {
    t.motion.twinkle *= 0.2;
    t.motion.drift *= 0.2;
    t.motion.pulse *= 0.2;
    t.fx.shimmer *= 0.2;
    t.fx.ripples = false;
    t.lines.energy *= 0.3;
  }

  if (powerSaver) {
    // 🔥 FIX: Power Saver should CALM the universe, not put it in a grey hoodie
    // Preserve: color identity, glow profile, theme character
    // Reduce: motion, particle count, shimmer frequency
    
    // Reduce starfield density (less particles to render)
    t.bg.starfieldDensity = Math.max(40, Math.floor(t.bg.starfieldDensity * 0.5));
    
    // Reduce motion intensity (slower animations)
    t.motion.twinkle *= 0.4;
    t.motion.drift *= 0.3;
    t.motion.pulse *= 0.4;
    
    // Reduce shimmer frequency but keep it visible
    t.fx.shimmer *= 0.35;
    
    // Disable tap ripples (saves animation frames)
    t.fx.ripples = false;
    
    // Reduce line energy animation
    t.lines.energy *= 0.4;
    
    // 🎨 PRESERVE: Glow and aura stay at full theme intensity
    // (No longer reducing t.fx.glow - keeps theme color identity)
  }

  return t;
}
